/**
 * Pragurile minime ale unei comenzi de preluare a deseurilor.
 *
 * 🔴 SURSA DE ADEVAR E BACKENDUL, nu fisierul asta:
 * `backend/src/main/java/com/backend/ecotic/validator/WasteCollectionFormValidator.java`,
 * metoda `validateAndCheckWasteMinimumQuantity` (`minDeeeKg`, `minDbaKg`, `minBecuriSiNeoaneKg`).
 * Serverul refuza cu 409 o comanda care nu atinge CEL PUTIN UNUL dintre praguri. Pana aici,
 * formularul trecea de validarea clientului si de dialogul «Confirmare trimitere», iar omul afla
 * abia din toastul de dupa (masurat pe DEV 2026-09-15, N6: DEEE 2.5 / DBA 5 / becuri 0.5 → 409).
 *
 * Valorile si mesajul sunt copiate, deci pot diverge. `praguri-comanda.test.ts` citeste sursa
 * validatorului si pica daca o cifra sau mesajul nu mai coincid.
 *
 * Regula e «cel putin una din trei», nu un minim pe fiecare camp: de aceea eroarea e una singura,
 * pe grup (`root.cantitati`), si nu se ataseaza unui camp anume.
 */
export const PRAGURI_MINIME_KG = {
  deeeKg: 150,
  dbaKg: 30,
  becuriSiNeoaneKg: 5,
} as const;

/**
 * Textul exact al serverului, AD LITTERAM (directia S1): la un 409 omul vede aceeasi formulare
 * ca pe client, nu doua variante ale aceleiasi erori. De aceea e fara diacritice.
 */
export const MESAJ_PRAG_NEATINS =
  'Formular refuzat, cel putin o conditie trebuie respectata: cantitatea minima DEEE 150kg, cantitatea minima DBA 30kg, sau cantitatea minima NEOANE SI BECURI 5kg';

/** Cheia erorii de grup in react-hook-form. `root.*` nu e sters de validarea unui camp la blur. */
export const CHEIE_EROARE_PRAG = 'root.cantitati' as const;

/** Randul informativ de sub «Cantități deșeuri», afisat inainte de completare. */
export const TEXT_PRAGURI =
  `Cel puțin o cantitate trebuie să atingă pragul minim: DEEE ${PRAGURI_MINIME_KG.deeeKg} kg, ` +
  `DBA ${PRAGURI_MINIME_KG.dbaKg} kg sau becuri și neoane ${PRAGURI_MINIME_KG.becuriSiNeoaneKg} kg.`;

/** Campurile formularului vin ca string din input (`e.target.value`), ca numar dupa Zod, sau lipsesc. */
type Cantitati = { deeeKg?: unknown; dbaKg?: unknown; becuriSiNeoaneKg?: unknown };

const kg = (v: unknown): number => {
  if (v === undefined || v === null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * `true` cand cel putin o cantitate atinge pragul. Pragurile sunt intregi, deci compararea pe
 * valoarea zecimala din formular da acelasi rezultat ca serverul, care trunchiaza la `Integer`
 * (149.9 → 149 < 150, iar 30.0 → 30 ≥ 30).
 */
export function pragAtins(c: Cantitati): boolean {
  return (
    kg(c.deeeKg) >= PRAGURI_MINIME_KG.deeeKg ||
    kg(c.dbaKg) >= PRAGURI_MINIME_KG.dbaKg ||
    kg(c.becuriSiNeoaneKg) >= PRAGURI_MINIME_KG.becuriSiNeoaneKg
  );
}
